import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { crmAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Send, Copy, MessageSquare, Check,
  SkipForward, Loader2, ChevronLeft, Users, FileText,
  Plus, Trash2, X, Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

// ─── Step 1: Compose ────────────────────────────────
const ComposeStep = ({ contacts, onStartQueue, onBack }) => {
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('personal');
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    crmAPI.getMessageTemplates().then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const handleUseTemplate = (content) => {
    setMessage(content);
    toast.success('Template loaded');
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !message.trim()) return;
    try {
      const r = await crmAPI.createMessageTemplate({ name: templateName, content: message });
      setTemplates(prev => [r.data, ...prev]);
      setShowSaveTemplate(false);
      setTemplateName('');
      toast.success('Template saved');
    } catch { toast.error('Failed to save template'); }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await crmAPI.deleteMessageTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleStart = async () => {
    if (!message.trim()) { toast.error('Enter a message'); return; }
    setCreating(true);
    try {
      const contactIds = contacts.map(c => c.id);
      const r = await crmAPI.createSendQueue({ contact_ids: contactIds, message, mode });
      onStartQueue(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create queue');
    } finally { setCreating(false); }
  };

  const previewMessage = message.replace(/\{\{name\}\}/g, contacts[0]?.name || 'John');

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="compose-back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Compose Message
          </h2>
          <p className="text-sm text-zinc-500">{contacts.length} contact{contacts.length !== 1 ? 's' : ''} selected</p>
        </div>
      </div>

      {/* Templates */}
      {templates.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Saved Templates</p>
          <div className="flex flex-wrap gap-2">
            {templates.map(t => (
              <div key={t.id} className="group flex items-center gap-1 bg-zinc-50 border border-zinc-200 rounded-full px-3 py-1.5">
                <button onClick={() => handleUseTemplate(t.content)}
                  className="text-xs font-medium text-zinc-700 hover:text-zinc-900" data-testid={`use-template-${t.id}`}>
                  {t.name}
                </button>
                <button onClick={() => handleDeleteTemplate(t.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-1" data-testid={`delete-template-${t.id}`}>
                  <X className="w-3 h-3 text-zinc-400 hover:text-red-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-zinc-700">Message</label>
          <button onClick={() => {
            const ta = document.getElementById('sq-message');
            const pos = ta?.selectionStart || message.length;
            setMessage(message.slice(0, pos) + '{{name}}' + message.slice(pos));
          }} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="insert-name-token-btn">
            + Insert {'{{name}}'}
          </button>
        </div>
        <Textarea id="sq-message" value={message} onChange={e => setMessage(e.target.value)}
          placeholder="Hey {{name}}! Quick question — are you still looking for that ring?"
          rows={4} data-testid="queue-message-input" className="text-sm" />
        <div className="flex items-center justify-between">
          <p className="text-xs text-zinc-400">{message.length} characters</p>
          <button onClick={() => setShowSaveTemplate(true)}
            className="text-xs text-zinc-500 hover:text-zinc-700 flex items-center gap-1" data-testid="save-template-btn">
            <FileText className="w-3 h-3" /> Save as template
          </button>
        </div>
      </div>

      {/* Preview */}
      {message && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Preview</p>
          <p className="text-sm text-zinc-800 whitespace-pre-wrap" data-testid="message-preview">{previewMessage}</p>
        </div>
      )}

      {/* Mode Selection */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => setMode('personal')} data-testid="mode-personal-btn"
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            mode === 'personal' ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}>
          <MessageSquare className={`w-5 h-5 mb-2 ${mode === 'personal' ? 'text-white' : 'text-zinc-600'}`} />
          <p className="font-medium text-sm">Personal</p>
          <p className={`text-xs mt-1 ${mode === 'personal' ? 'text-zinc-300' : 'text-zinc-500'}`}>
            Send one-by-one via iMessage
          </p>
        </button>
        <button onClick={() => setMode('bulk')} data-testid="mode-bulk-btn"
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            mode === 'bulk' ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}>
          <Zap className={`w-5 h-5 mb-2 ${mode === 'bulk' ? 'text-white' : 'text-zinc-600'}`} />
          <p className="font-medium text-sm">Bulk</p>
          <p className={`text-xs mt-1 ${mode === 'bulk' ? 'text-zinc-300' : 'text-zinc-500'}`}>
            Send all via Linq (iMessage/SMS)
          </p>
        </button>
      </div>

      {/* Start Button */}
      <Button onClick={handleStart} disabled={creating || !message.trim()}
        className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-12 text-sm"
        data-testid="start-queue-btn">
        {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        {mode === 'personal' ? 'Start Sending' : 'Preview & Send Bulk'}
      </Button>

      {/* Save Template Dialog */}
      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent className="sm:max-w-sm bg-white">
          <DialogHeader><DialogTitle>Save Template</DialogTitle></DialogHeader>
          <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)}
            placeholder="Template name..." data-testid="template-name-input"
            className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-200" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-save-template-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Step 2a: Personal Mode ─────────────────────────
const PersonalMode = ({ queue, onComplete, onBack }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState(queue.items || []);
  const [updating, setUpdating] = useState(false);
  const [counts, setCounts] = useState({ sent: queue.sent, skipped: queue.skipped, pending: queue.pending });

  // Jump to the first pending item
  useEffect(() => {
    const firstPendingIdx = items.findIndex(i => i.status === 'pending');
    if (firstPendingIdx >= 0) setCurrentIndex(firstPendingIdx);
  }, []);

  const current = items[currentIndex];
  const total = items.length;

  const handleCopyAndOpen = () => {
    navigator.clipboard.writeText(current.personalized_message);
    toast.success('Message copied to clipboard');
    const phone = current.contact_phone.replace(/\D/g, '');
    const smsUrl = `sms:+${phone.startsWith('1') ? phone : '1' + phone}`;
    window.open(smsUrl, '_self');
  };

  const updateItemStatus = async (status) => {
    setUpdating(true);
    try {
      const r = await crmAPI.updateQueueItemStatus(queue.id, current.id, status);
      const updatedItems = [...items];
      updatedItems[currentIndex] = { ...updatedItems[currentIndex], status };
      setItems(updatedItems);
      setCounts({ sent: r.data.sent, skipped: r.data.skipped, pending: r.data.pending });

      if (r.data.status === 'completed') {
        onComplete(r.data);
        return;
      }
      // Move to next pending
      const nextPending = updatedItems.findIndex((item, idx) => idx > currentIndex && item.status === 'pending');
      if (nextPending >= 0) setCurrentIndex(nextPending);
      else {
        // Try from beginning
        const fromStart = updatedItems.findIndex(item => item.status === 'pending');
        if (fromStart >= 0) setCurrentIndex(fromStart);
        else onComplete(r.data);
      }
    } catch { toast.error('Failed to update status'); }
    finally { setUpdating(false); }
  };

  const goToIndex = (idx) => {
    if (idx >= 0 && idx < total) setCurrentIndex(idx);
  };

  if (!current) return null;

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="personal-back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Personal Send
          </h2>
        </div>
        <div className="flex items-center gap-3 text-xs font-medium">
          <span className="text-emerald-600">{counts.sent} sent</span>
          <span className="text-zinc-400">{counts.skipped} skipped</span>
          <span className="text-amber-600">{counts.pending} left</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full bg-zinc-900 transition-all duration-300 rounded-full"
          style={{ width: `${((counts.sent + counts.skipped) / total) * 100}%` }} />
      </div>

      {/* Current Contact Card */}
      <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5" data-testid="personal-send-card">
        <div className="text-center space-y-1">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Sending to</p>
          <h3 className="text-xl font-semibold text-zinc-900" data-testid="current-contact-name">
            {current.contact_name}
          </h3>
          <p className="text-sm text-zinc-500 font-mono" data-testid="current-contact-phone">
            {current.contact_phone}
          </p>
          {current.status !== 'pending' && (
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${
              current.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'
            }`}>{current.status}</span>
          )}
        </div>

        <div className="bg-zinc-50 rounded-lg p-4">
          <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed" data-testid="current-message">
            {current.personalized_message}
          </p>
        </div>

        {current.status === 'pending' && (
          <Button onClick={handleCopyAndOpen}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
            data-testid="copy-open-imessage-btn">
            <Copy className="w-4 h-4 mr-2" /> Copy & Open iMessage
          </Button>
        )}
      </div>

      {/* Navigation + Actions */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => goToIndex(currentIndex - 1)}
          disabled={currentIndex === 0} data-testid="prev-contact-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Previous
        </Button>

        <span className="text-sm font-medium text-zinc-500" data-testid="queue-progress">
          {currentIndex + 1} of {total}
        </span>

        <Button variant="outline" size="sm" onClick={() => goToIndex(currentIndex + 1)}
          disabled={currentIndex === total - 1} data-testid="next-contact-btn">
          Next <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {current.status === 'pending' && (
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" disabled={updating}
            onClick={() => updateItemStatus('skipped')} data-testid="skip-contact-btn">
            <SkipForward className="w-4 h-4 mr-2" /> Skip
          </Button>
          <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={updating}
            onClick={() => updateItemStatus('sent')} data-testid="mark-sent-btn">
            {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Mark as Sent
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Step 2b: Bulk Mode ─────────────────────────────
const BulkMode = ({ queue, onComplete, onBack }) => {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSendAll = async () => {
    setSending(true);
    try {
      const r = await crmAPI.sendQueueBulk(queue.id);
      setResult(r.data);
      if (r.data.success > 0) toast.success(`Sent to ${r.data.success} contacts`);
      if (r.data.failed > 0) toast.error(`Failed for ${r.data.failed} contacts`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  if (result) {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Bulk Send Complete
          </h2>
          <div className="flex items-center justify-center gap-6 text-sm">
            <span className="text-emerald-600 font-medium">{result.success} sent</span>
            <span className="text-red-500 font-medium">{result.failed} failed</span>
          </div>
          {result.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-left">
              <p className="text-xs font-semibold text-red-700 mb-1">Errors:</p>
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs text-red-600">{e}</p>
              ))}
            </div>
          )}
          <Button onClick={() => onComplete(result)} className="bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="bulk-done-btn">
            Done
          </Button>
        </div>
      </div>
    );
  }

  const pendingItems = queue.items.filter(i => i.status === 'pending');

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="bulk-back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Bulk Send Preview
          </h2>
          <p className="text-sm text-zinc-500">{pendingItems.length} message{pendingItems.length !== 1 ? 's' : ''} to send via Linq</p>
        </div>
      </div>

      {/* Recipients Preview */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-5 py-2.5 bg-zinc-50 border-b border-zinc-200 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Phone</div>
          <div className="col-span-6">Message</div>
        </div>
        <div className="divide-y divide-zinc-100 max-h-[400px] overflow-y-auto">
          {pendingItems.map(item => (
            <div key={item.id} className="grid grid-cols-12 gap-4 px-5 py-3 items-start" data-testid={`bulk-item-${item.id}`}>
              <div className="col-span-3 text-sm font-medium text-zinc-900 truncate">{item.contact_name}</div>
              <div className="col-span-3 text-sm text-zinc-500 font-mono">{item.contact_phone}</div>
              <div className="col-span-6 text-sm text-zinc-700 whitespace-pre-wrap leading-snug">{item.personalized_message}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Send Button */}
      <Button onClick={handleSendAll} disabled={sending}
        className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-12" data-testid="send-all-clicksend-btn">
        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
        Send All via Linq ({pendingItems.length} messages)
      </Button>
    </div>
  );
};

// ─── Completion Screen ──────────────────────────────
const CompletedScreen = ({ counts, onBackToContacts }) => (
  <div className="max-w-lg mx-auto text-center py-12 space-y-6">
    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
      <Check className="w-10 h-10 text-emerald-600" />
    </div>
    <h2 className="text-3xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
      Queue Complete
    </h2>
    <div className="flex items-center justify-center gap-8 text-sm">
      <div className="text-center">
        <p className="text-2xl font-semibold text-emerald-600">{counts.sent}</p>
        <p className="text-zinc-500">Sent</p>
      </div>
      <div className="text-center">
        <p className="text-2xl font-semibold text-zinc-400">{counts.skipped}</p>
        <p className="text-zinc-500">Skipped</p>
      </div>
    </div>
    <Button onClick={onBackToContacts} className="bg-zinc-900 hover:bg-zinc-800 text-white"
      data-testid="back-to-contacts-btn">
      <Users className="w-4 h-4 mr-2" /> Back to Contacts
    </Button>
  </div>
);

// ─── Main Send Queue Page ───────────────────────────
const SendQueue = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('compose'); // compose, processing, completed
  const [contacts, setContacts] = useState([]);
  const [queue, setQueue] = useState(null);
  const [completedCounts, setCompletedCounts] = useState({ sent: 0, skipped: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ids = searchParams.get('contacts');
    if (!ids) { navigate('/contacts'); return; }
    const contactIds = ids.split(',');
    
    const fetchContacts = async () => {
      try {
        const r = await crmAPI.getContacts({});
        const all = r.data || [];
        const selected = all.filter(c => contactIds.includes(c.id));
        if (selected.length === 0) { toast.error('No contacts found'); navigate('/contacts'); return; }
        setContacts(selected);
      } catch { toast.error('Failed to load contacts'); navigate('/contacts'); }
      finally { setLoading(false); }
    };
    fetchContacts();
  }, [searchParams, navigate]);

  const handleStartQueue = (queueData) => {
    setQueue(queueData);
    setStep('processing');
  };

  const handleComplete = (data) => {
    setCompletedCounts({ sent: data.sent || data.success || 0, skipped: data.skipped || 0 });
    setStep('completed');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="send-queue-loading">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="send-queue-page">
      {step === 'compose' && (
        <ComposeStep contacts={contacts} onStartQueue={handleStartQueue} onBack={() => navigate('/contacts')} />
      )}
      {step === 'processing' && queue && queue.mode === 'personal' && (
        <PersonalMode queue={queue} onComplete={handleComplete} onBack={() => setStep('compose')} />
      )}
      {step === 'processing' && queue && queue.mode === 'bulk' && (
        <BulkMode queue={queue} onComplete={handleComplete} onBack={() => setStep('compose')} />
      )}
      {step === 'completed' && (
        <CompletedScreen counts={completedCounts} onBackToContacts={() => navigate('/contacts')} />
      )}
    </div>
  );
};

export default SendQueue;
